import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box, Typography, Paper, Switch, FormControlLabel, TextField,
  Button, Grid, Card, CardContent, CardActionArea, Chip, MenuItem,
  Select, FormControl, InputLabel, Divider, Alert, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton,
} from "@mui/material";
import { Delete, Add, Language, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import {
  useGetWebsiteManageSettingsQuery,
  useUpdateWebsiteSettingsMutation,
  useGetCmsPagesQuery,
  useGetWebsiteDomainSettingsQuery,
  useUpdateWebsiteDomainSettingsMutation,
  useDeleteWebsiteDomainSettingsMutation,
  useRetryWebsiteDomainSettingsMutation,
} from "../../../app/websiteApiSlice";
import { selectCurrentUser } from "../auth/authSlice";
import toast from "../../../helpers/toast";

const THEMES = [
  {
    id: "modern",
    name: "Modern / Minimal",
    description: "Clean white layout, spacious typography, minimal navigation",
    color: "#2196f3",
  },
  {
    id: "classic",
    name: "Classic / Academic",
    description: "Traditional serif aesthetic, sidebar layout, school crest prominent",
    color: "#795548",
  },
  {
    id: "bold",
    name: "Bold / Magazine",
    description: "Large hero banner, colourful category chips, dynamic grid layout",
    color: "#e91e63",
  },
];

export default function CmsSettings() {
  const user = useSelector(selectCurrentUser);
  const isAdmin = user?.roles?.some((r) => r.role?.name === "admin");

  const { data: settings, isLoading } = useGetWebsiteManageSettingsQuery();
  const { data: domainSettings } = useGetWebsiteDomainSettingsQuery();
  const { data: pages = [] } = useGetCmsPagesQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateWebsiteSettingsMutation();
  const [updateDomain, { isLoading: savingDomain }] = useUpdateWebsiteDomainSettingsMutation();
  const [deleteDomain, { isLoading: removingDomain }] = useDeleteWebsiteDomainSettingsMutation();
  const [retryDomain, { isLoading: retryingDomain }] = useRetryWebsiteDomainSettingsMutation();

  const publishedPages = pages.filter((p) => p.status === "published");

  const [form, setForm] = useState({
    enabled: false,
    theme: "modern",
    tagline: "",
    footer_text: "",
    home_page_id: "",
    posts_page_id: "",
  });

  const [navItems, setNavItems] = useState([]);
  const [newNav, setNewNav] = useState({ label: "", url: "" });
  const [customDomain, setCustomDomain] = useState("");

  useEffect(() => {
    if (settings) {
      setForm({
        enabled: settings.enabled ?? false,
        theme: settings.theme ?? "modern",
        tagline: settings.tagline ?? "",
        footer_text: settings.footer_text ?? "",
        home_page_id: settings.home_page_id ?? settings.home_page?.id ?? "",
        posts_page_id: settings.posts_page_id ?? settings.posts_page?.id ?? "",
      });
      setNavItems(settings.nav_items ?? []);
    }
  }, [settings]);

  useEffect(() => {
    setCustomDomain(domainSettings?.custom_domain ?? "");
  }, [domainSettings]);

  function handleAddNav() {
    if (!newNav.label || !newNav.url) return;
    setNavItems((prev) => [...prev, { label: newNav.label, url: newNav.url }]);
    setNewNav({ label: "", url: "" });
  }

  function handleRemoveNav(index) {
    setNavItems((prev) => prev.filter((_, i) => i !== index));
  }

  function handleMoveNav(index, dir) {
    setNavItems((prev) => {
      const arr = [...prev];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  }

  async function handleSave() {
    try {
      const payload = {
        ...form,
        home_page_id: form.home_page_id || null,
        posts_page_id: form.posts_page_id || null,
        nav_items: navItems,
      };
      // Non-admins cannot send the enabled field
      if (!isAdmin) delete payload.enabled;
      await updateSettings(payload).unwrap();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save settings");
    }
  }

  async function handleSaveDomain() {
    try {
      await updateDomain({ domain: customDomain }).unwrap();
      toast.success("Custom domain saved");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to save custom domain");
    }
  }

  async function handleRemoveDomain() {
    try {
      await deleteDomain().unwrap();
      setCustomDomain("");
      toast.success("Custom domain removed");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to remove custom domain");
    }
  }

  async function handleRetryDomain() {
    try {
      await retryDomain().unwrap();
      toast.success("Domain sync retried");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to retry domain sync");
    }
  }

  const domainBusy = savingDomain || removingDomain || retryingDomain;
  const statusColorByValue = {
    not_configured: "default",
    pending: "warning",
    pending_deploy: "warning",
    syncing: "info",
    synced: "success",
    failed: "error",
  };
  const cooldownUntil =
    domainSettings?.status === "synced" && domainSettings?.last_synced_at
      ? new Date(new Date(domainSettings.last_synced_at).getTime() + 24 * 60 * 60 * 1000)
      : null;
  const isCooldownActive = cooldownUntil ? cooldownUntil.getTime() > Date.now() : false;
  const cooldownMinutes = isCooldownActive
    ? Math.max(1, Math.ceil((cooldownUntil.getTime() - Date.now()) / 60000))
    : 0;
  const cooldownHoursPart = Math.floor(cooldownMinutes / 60);
  const cooldownMinutesPart = cooldownMinutes % 60;
  const cooldownText = isCooldownActive
    ? [
        cooldownHoursPart ? `${cooldownHoursPart} hour${cooldownHoursPart === 1 ? "" : "s"}` : null,
        cooldownMinutesPart ? `${cooldownMinutesPart} minute${cooldownMinutesPart === 1 ? "" : "s"}` : null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  if (isLoading) return <Box p={3}><Typography>Loading...</Typography></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Website Settings</Typography>

      {/* Enable / Disable */}
      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            <Language sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Website Status
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                color="success"
              />
            }
            label={form.enabled ? "Website is LIVE — visitors see the school site at /" : "Website is OFF — visitors are redirected to login"}
          />
          {form.enabled && (
            <Alert severity="info" sx={{ mt: 1 }}>
              When enabled, unauthenticated visitors will see your school website at the root URL.
              The login page is still accessible via the "Login" link in the navigation.
            </Alert>
          )}
        </Paper>
      )}

      {/* Theme picker */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Theme</Typography>
        <Grid container spacing={2}>
          {THEMES.map((theme) => (
            <Grid item xs={12} sm={4} key={theme.id}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: form.theme === theme.id ? "primary.main" : "divider",
                  borderWidth: form.theme === theme.id ? 2 : 1,
                  cursor: "pointer",
                }}
              >
                <CardActionArea onClick={() => setForm((f) => ({ ...f, theme: theme.id }))}>
                  <Box sx={{ height: 6, bgcolor: theme.color }} />
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="subtitle2" fontWeight={600}>{theme.name}</Typography>
                      {form.theme === theme.id && (
                        <Chip label="Active" size="small" color="primary" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {theme.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Homepage / Posts page */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Page Assignments</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Home Page</InputLabel>
              <Select
                value={form.home_page_id ?? ""}
                label="Home Page"
                onChange={(e) => setForm((f) => ({ ...f, home_page_id: e.target.value || "" }))}
              >
                <MenuItem value="">-- Default (show recent posts) --</MenuItem>
                {publishedPages.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Posts Page</InputLabel>
              <Select
                value={form.posts_page_id ?? ""}
                label="Posts Page"
                onChange={(e) => setForm((f) => ({ ...f, posts_page_id: e.target.value || "" }))}
              >
                <MenuItem value="">-- None --</MenuItem>
                {publishedPages.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tagline & Footer */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Site Identity</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Tagline"
              value={form.tagline}
              onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
              fullWidth size="small"
              placeholder="e.g. Excellence in Education"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Footer Text"
              value={form.footer_text}
              onChange={(e) => setForm((f) => ({ ...f, footer_text: e.target.value }))}
              fullWidth size="small" multiline rows={2}
              placeholder="e.g. © 2026 Springfield Academy. All rights reserved."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Custom Domain</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Original Doploy Domain"
              value={domainSettings?.initial_domain ?? ""}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              helperText="This default domain always stays attached."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Custom Domain"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              fullWidth
              size="small"
              placeholder="school.com"
              helperText="Enter a bare hostname only, such as school.com or www.school.com."
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={(domainSettings?.status || "not_configured").replaceAll("_", " ")}
                color={statusColorByValue[domainSettings?.status || "not_configured"]}
                size="small"
              />
              {domainSettings?.last_synced_at && (
                <Typography variant="caption" color="text.secondary">
                  Last synced: {new Date(domainSettings.last_synced_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Grid>
          {domainSettings?.error && (
            <Grid item xs={12}>
              <Alert severity="error">{domainSettings.error}</Alert>
            </Grid>
          )}
          {(domainSettings?.status === "pending_deploy" || domainSettings?.status === "syncing") && (
            <Grid item xs={12}>
              <Alert severity="info">
                {domainSettings.status === "pending_deploy"
                  ? "The request is saved and will be applied after the school is deployed."
                  : "Doploy is currently syncing the custom domain."}
              </Alert>
            </Grid>
          )}
          {isCooldownActive && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Domain settings can be changed again in {cooldownText}.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={handleSaveDomain}
                disabled={domainBusy || isCooldownActive}
              >
                {savingDomain ? "Saving..." : "Save Domain"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveDomain}
                disabled={domainBusy || isCooldownActive || !domainSettings?.custom_domain}
              >
                {removingDomain ? "Removing..." : "Remove Domain"}
              </Button>
              <Button
                variant="text"
                onClick={handleRetryDomain}
                disabled={domainBusy || !["failed", "pending_deploy"].includes(domainSettings?.status)}
              >
                {retryingDomain ? "Retrying..." : "Retry Sync"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation builder */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Navigation Menu</Typography>
        <List dense disablePadding>
          {navItems.map((item, index) => (
            <ListItem key={index} disableGutters divider>
              <ListItemText primary={item.label} secondary={item.url} />
              <ListItemSecondaryAction>
                <IconButton size="small" onClick={() => handleMoveNav(index, -1)} disabled={index === 0}>
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleMoveNav(index, 1)} disabled={index === navItems.length - 1}>
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleRemoveNav(index)}>
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {navItems.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No nav items yet. The "Login" link is always included automatically.
            </Typography>
          )}
        </List>
        <Divider sx={{ my: 1.5 }} />
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            label="Label"
            value={newNav.label}
            onChange={(e) => setNewNav((n) => ({ ...n, label: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="URL"
            value={newNav.url}
            onChange={(e) => setNewNav((n) => ({ ...n, url: e.target.value }))}
            size="small"
            placeholder="/p/about"
            sx={{ flex: 2 }}
          />
          <IconButton onClick={handleAddNav} color="primary">
            <Add />
          </IconButton>
        </Box>
      </Paper>

      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}
